import { useTranslate } from '@refinedev/core';
import { baseEmotionsEn, baseEmotionsRu } from '@cc/schema';
import { Button, Checkbox, Input, Select, Space } from 'antd';
import { useMemo } from 'react';
import {
  KIND_CHOICES,
  SKILLS,
  UW_EMOTIONS,
  newTargetId,
  patchTarget,
  type CardDraft,
} from './model';

/**
 * E37 · the diary card's ROW EDITOR — shared by the practice's "Set up card" (DefineDiaryCard, the client hub) and the
 * backoffice's template publisher (PublishDiaryCardTemplate): add, rename or remove rows — a scale 0–5 (Linehan's, the
 * default) or 0–10, a yes/no, a count (also for an action) or a short text —, choose the emotion rows (the UW card's three
 * and the app's emotion groups) and the skills by module. Controlled: the caller owns the draft and the Save.
 */
type Props = {
  draft: CardDraft;
  onChange: (next: CardDraft) => void;
  locale: 'en' | 'ru';
  idPrefix: string;
};

export const CardEditor = ({ draft, onChange, locale, idPrefix }: Props) => {
  const t = useTranslate();
  const groups = locale === 'ru' ? baseEmotionsRu : baseEmotionsEn;
  const emotionOptions = useMemo(
    () => [
      ...UW_EMOTIONS.map((id) => ({
        value: id,
        label: t(`diaryCard.emotion.${id}`),
      })),
      ...groups.map((g) => ({ value: String(g.id), label: g.name })),
    ],
    [groups, t],
  );
  const skillOptions = useMemo(() => {
    const byModule = new Map<string, Array<{ value: string; label: string }>>();
    for (const s of SKILLS) {
      const list = byModule.get(s.module) ?? [];
      list.push({ value: s.skillId, label: t(`diaryCard.skill.${s.skillId}`) });
      byModule.set(s.module, list);
    }
    return Array.from(byModule.entries()).map(([module, options]) => ({
      label: t(`diaryCard.module.${module}`),
      options,
    }));
  }, [t]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {draft.targets.map((target) => (
        <Space
          key={target.targetId}
          wrap
          data-testid={`${idPrefix}-row-${target.targetId}`}
        >
          <Input
            data-testid={`${idPrefix}-name-${target.targetId}`}
            onChange={(e) =>
              onChange(patchTarget(draft, target.targetId, { name: e.target.value }))
            }
            placeholder={t('diaryCard.hub.rowNamePlaceholder')}
            style={{ width: 260 }}
            value={target.name}
          />
          <Select
            data-testid={`${idPrefix}-kind-${target.targetId}`}
            onChange={(kind) => onChange(patchTarget(draft, target.targetId, { kind }))}
            options={KIND_CHOICES.map((k) => ({
              value: k,
              label: t(`diaryCard.hub.kind.${k}`),
            }))}
            style={{ width: 150 }}
            value={target.kind}
          />
          {target.kind === 'urge' || target.kind === 'scale' ? (
            <Select
              data-testid={`${idPrefix}-max-${target.targetId}`}
              onChange={(max) => onChange(patchTarget(draft, target.targetId, { max }))}
              options={[5, 10].map((max) => ({
                value: max,
                label: t('diaryCard.hub.scaleTo', { max }),
              }))}
              style={{ width: 110 }}
              value={target.max ?? 5}
            />
          ) : null}
          {target.kind === 'action' ? (
            <Checkbox
              checked={!!target.countable}
              data-testid={`${idPrefix}-countable-${target.targetId}`}
              onChange={(e) =>
                onChange(
                  patchTarget(draft, target.targetId, { countable: e.target.checked }),
                )
              }
            >
              {t('diaryCard.hub.countable')}
            </Checkbox>
          ) : null}
          <Button
            danger
            data-testid={`${idPrefix}-remove-${target.targetId}`}
            onClick={() =>
              onChange({
                ...draft,
                targets: draft.targets.filter((x) => x.targetId !== target.targetId),
              })
            }
            size="small"
          >
            {t('diaryCard.hub.removeRow')}
          </Button>
        </Space>
      ))}
      <Button
        data-testid={`${idPrefix}-add`}
        onClick={() =>
          onChange({
            ...draft,
            targets: [
              ...draft.targets,
              { targetId: newTargetId(), name: '', kind: 'scale', max: 5 },
            ],
          })
        }
      >
        + {t('diaryCard.hub.addRow')}
      </Button>
      <div>
        <div>{t('diaryCard.hub.emotions')}</div>
        <Select
          data-testid={`${idPrefix}-emotions`}
          mode="multiple"
          onChange={(emotions) => onChange({ ...draft, emotions })}
          optionFilterProp="label"
          options={emotionOptions}
          style={{ width: '100%' }}
          value={draft.emotions}
        />
      </div>
      <div>
        <div>{t('diaryCard.hub.skills')}</div>
        <Select
          data-testid={`${idPrefix}-skills`}
          mode="multiple"
          onChange={(skills) => onChange({ ...draft, skills })}
          optionFilterProp="label"
          options={skillOptions}
          style={{ width: '100%' }}
          value={draft.skills}
        />
      </div>
    </Space>
  );
};
